import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import {
  AuthService,
  BlockAbstractComponent,
  DocumentService,
  ParserService,
  WindowService,
} from '@gallaghercp/core-frontend';

declare global {
  interface Window {
    _wq?: any[];
  }
}

@Component({
  selector: 'core-block-video',
  templateUrl: './block-video.component.html',
  standalone: false,
})
export class BlockVideoComponent extends BlockAbstractComponent implements OnInit, OnDestroy {
  protected override parser: ParserService;
  protected override auth: AuthService;
  protected bsModalRef = inject(BsModalRef);
  private sanitizer = inject(DomSanitizer);
  protected windowService = inject(WindowService);
  protected documentService = inject(DocumentService);

  videoUrl?: SafeResourceUrl;
  private languageSubscription?: Subscription;

  constructor() {
    const parser = inject(ParserService);
    const auth = inject(AuthService);

    super(parser, auth);

    this.parser = parser;
    this.auth = auth;
  }

  override ngOnInit() {
    super.ngOnInit();

    // Initialize config
    this.block.data.config = {
      vendors: [
        {
          vendor: 'Vimeo',
          src: '//player.vimeo.com/api/player.js',
          json: 'https://vimeo.com/api/oembed.json?url=https%3A//vimeo.com/',
        },
        {
          vendor: 'Wistia',
          src: '//fast.wistia.com/assets/external/E-v1.js',
          json: '//fast.wistia.net/oembed?url=http://home.wistia.com/medias/',
          name: 'wistia_embed',
        },
        {
          vendor: 'YouTube',
        },
        {
          vendor: 'BrightCove',
          src: '//players.brightcove.net/1752604059001/default_default/index.min.js',
          accountId: '1752604059001',
        },
      ],
      player: {
        allowfullscreen: true,
        name: '',
      },
      ratios: [
        {
          text: '16:9',
          value: 'ratio_16-9',
        },
        {
          text: '3:2',
          value: 'ratio_3-2',
        },
        {
          text: '4:3',
          value: 'ratio_4-3',
        },
      ],
      types: [
        {
          text: 'Inline',
          value: 'inline',
        },
        {
          text: 'Modal',
          value: 'modal',
        },
      ],
    };

    this.block.data.type = this.block.data.type || this.block.data.config.types[0].value;
    this.block.data.ratio = this.block.data.ratio || this.block.data.config.ratios[0].value;

    // Set the appropriate video ID based on language
    this.setVideoIdForCurrentLanguage();

    // Subscribe to language changes
    this.subscribeToLanguageChanges();

    // Initialize video URL
    this.initializeVideo();
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
  }

  private setVideoIdForCurrentLanguage(): void {
    if (this.block.data.multipleLanguage === 1 && this.block.data.languageVideos) {
      // Get current language using the inherited translateService
      const currentLanguage = this.translateService.getCurrentLanguage() as string;

      // Set the video ID based on current language
      if (this.block.data.languageVideos[currentLanguage]) {
        this.block.data.id = this.block.data.languageVideos[currentLanguage];
      } else {
        // Fallback to first available language if current language not found
        const firstAvailableLanguage = Object.keys(this.block.data.languageVideos).find(
          lang => this.block.data.languageVideos[lang] && lang !== 'content',
        );
        if (firstAvailableLanguage) {
          this.block.data.id = this.block.data.languageVideos[firstAvailableLanguage];
        }
      }
    }
  }

  private subscribeToLanguageChanges(): void {
    // Subscribe to language changes using translateProvider (ngx-translate)
    if (this.translateProvider && this.translateProvider.onLangChange) {
      this.languageSubscription = this.translateProvider.onLangChange.subscribe(() => {
        this.setVideoIdForCurrentLanguage();
        this.initializeVideo();
      });
    }
  }

  private initializeVideo(): void {
    const insertScript = (index: number): boolean => {
      const tag = this.documentService.createElement('script') as HTMLScriptElement | null;
      const scriptTags = this.documentService.getElementsByTagName('script');
      const lastScriptTag = scriptTags ? scriptTags[scriptTags.length - 1] : null;
      const className = 'SCRIPT-' + this.block.data.vendor;

      if (this.documentService.querySelector('.' + className)) {
        return false;
      }

      if (!tag) {
        return false; // SSR → safe exit
      }

      tag.setAttribute('class', className);
      tag.src = this.block.data.config.vendors[index].src;

      if (lastScriptTag?.parentNode) {
        lastScriptTag.parentNode.insertBefore(tag, lastScriptTag);
      }

      return true;
    };

    if (this.block.data.vendor === this.block.data.config.vendors[0].vendor) {
      this.block.data.url = '//player.vimeo.com/video/' + this.block.data.id;
      insertScript(0);
    } else if (this.block.data.vendor === this.block.data.config.vendors[1].vendor) {
      this.block.data.url = '//fast.wistia.net/embed/iframe/' + this.block.data.id + '?autoPlay=false';
      insertScript(1);
    } else if (this.block.data.vendor === this.block.data.config.vendors[2].vendor) {
      this.block.data.url = '//www.youtube.com/embed/' + this.block.data.id + '?enablejsapi=1&rel=0';
    } else if (this.block.data.vendor === this.block.data.config.vendors[3].vendor) {
      this.block.data.html5Video = true;
      this.block.data.accountId = this.block.data.config.vendors[3].accountId;
      insertScript(3);
    }

    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.block.data.url);

    setTimeout(() => {
      const id = 'video-player-' + this.block.uid;

      if (this.block.data.vendor === this.block.data.config.vendors[1].vendor) {
        this.block.data.config.player.name = this.block.data.config.vendors[1].name;
      }
    });
  }

  closeModal() {
    this.bsModalRef.hide();
  }
}
